import React from 'react';
import { VideoCall } from 'utils/videoCallTypes';
import { useVideoCallStore } from 'utils/videoCallStore';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  call: VideoCall;
  currentUserId: string;
  onAccept: () => void;
  onDecline: () => void;
}

export const IncomingCallModal: React.FC<Props> = ({ call, currentUserId, onAccept, onDecline }) => {
  const { showIncomingCallModal, setShowIncomingCallModal } = useVideoCallStore();
  
  // Get current user's role from participantDetails
  const currentUserRole = call.participantDetails?.[currentUserId]?.role;
  
  // Get all participants except current user
  const otherParticipants = call.participants
    .filter(id => id !== currentUserId)
    .map(id => call.participantDetails?.[id])
    .filter(Boolean);
  
  // Prioritize showing the client if one exists in the call
  const clientParticipant = otherParticipants.find(
    p => p.role === 'client' || p.role === 'limited_access'
  );
  
  // Use client if found, otherwise use the first other participant
  const otherParticipant = clientParticipant || otherParticipants[0];
  
  const getDisplayInfo = () => {
    // Clients always see "TAP Support Team"
    if (currentUserRole === 'client' || currentUserRole === 'limited_access') {
      return {
        name: 'TAP Support Team',
        photoURL: undefined,
        subtext: 'Incoming video call',
        helperText: 'This call is from TAP support staff'
      };
    }
    
    // Admins/Staff see the actual caller - prioritize showing client
    return {
      name: otherParticipant?.name || 'Unknown Caller',
      photoURL: otherParticipant?.photoURL,
      subtext: `${otherParticipant?.role || 'User'} • Video call`,
      helperText: 'Accept to start the video call'
    };
  };
  
  const displayInfo = getDisplayInfo();

  return (
    <Dialog open={showIncomingCallModal} onOpenChange={setShowIncomingCallModal}>
      <DialogContent className="sm:max-w-md">
        <div className="flex flex-col items-center space-y-6 py-6">
          {/* Caller Avatar */}
          <div className="relative">
            <Avatar className="h-24 w-24 ring-4 ring-blue-100">
              <AvatarImage src={displayInfo.photoURL} />
              <AvatarFallback className="bg-blue-500 text-white text-2xl">
                {displayInfo.name.charAt(0) || '?'}
              </AvatarFallback>
            </Avatar>
            
            {/* Pulsing ring animation */}
            <div className="absolute inset-0 h-24 w-24 rounded-full bg-blue-400 animate-ping opacity-20" />
            <div className="absolute inset-0 h-24 w-24 rounded-full bg-blue-400 animate-ping opacity-10 animation-delay-200" />
          </div>
          
          {/* Caller Info */}
          <div className="text-center space-y-1">
            <h3 className="text-xl font-semibold text-gray-900">
              {displayInfo.name}
            </h3>
            <p className="text-sm text-gray-500">
              {displayInfo.subtext}
            </p>
          </div>
          
          {/* Call Type Indicator */}
          <div className="flex items-center space-x-2 px-3 py-1 bg-blue-50 rounded-full">
            <Video className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-blue-600 font-medium">Video Call</span>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center space-x-6">
            {/* Decline Button */}
            <Button
              size="lg"
              onClick={onDecline}
              className={cn(
                "h-14 w-14 rounded-full",
                "bg-red-500 hover:bg-red-600",
                "focus:ring-2 focus:ring-red-200"
              )}
            >
              <div className="flex items-center justify-center">
                <PhoneOff className="h-6 w-6 text-white" />
              </div>
            </Button>
            
            {/* Accept Button */}
            <Button
              size="lg"
              onClick={onAccept}
              className={cn(
                "h-14 w-14 rounded-full",
                "bg-green-500 hover:bg-green-600",
                "focus:ring-2 focus:ring-green-200"
              )}
            >
              <div className="flex items-center justify-center">
                <Phone className="h-6 w-6 text-white" />
              </div>
            </Button>
          </div>
          
          {/* Helper Text */}
          <p className="text-xs text-gray-400 text-center max-w-xs">
            {displayInfo.helperText}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
